import { useEffect, type RefObject } from 'react';

/**
 * Der Fokuskäfig eines modalen Dialogs — Handbuch §3.193.
 *
 * **Der Befund, wörtlich aus dem Kopfkommentar von `Dialog` in `elements.tsx`:**
 * „Der Radix-Dialog kann drei Dinge, die dieser nicht kann: Er hält den Fokus
 * fest, gibt ihn beim Schliessen zurück und macht alles ausserhalb unerreichbar.
 * Das erste und zweite fehlen hier wirklich, und das ist ein offener Befund. […]
 * Der Fokuskäfig gehört trotzdem nachgeholt — dann aber als eigener, geprüfter
 * Haken und nicht als Nebenwirkung einer Abhängigkeit." Genau das ist diese
 * Datei: kein Radix-Dialog, kein `aria-hidden` auf den Rest der Seite (das war
 * der Grund, ihn NICHT zu nehmen — Playwrights Rollensuche überspringt versteckte
 * Knoten, und mehrere Prüfungen sprechen Elemente hinter einem offenen Dialog
 * über `page.getByRole(...)` an), aber die zwei fehlenden Stücke: Einschluss und
 * Rückgabe.
 *
 * **Warum eine reine Funktion und ein Haken, statt alles in einem `useEffect`.**
 * Dieses Testprojekt hat sich ausdrücklich gegen `jsdom` und eine
 * Rendering-Bibliothek entschieden (Aufgabe 45, `apps/web/test/anlegedialog.test.ts`):
 * geprüft wird, was gerendert wird, von Playwright am echten Browser. Die
 * Tabreihenfolge selbst — welcher Index nach vorn/zurück als nächstes dran ist,
 * zyklisch am Rand — ist aber eine reine Rechnung ohne jedes DOM-Bedürfnis, genau
 * wie `luecken.ts` seine Fallunterscheidung aus dem `.tsx` herausgezogen hat. Sie
 * steht deshalb als `naechsterFokusIndex` für sich und ist die einzige Zusicherung
 * dieser Datei, die `apps/web/test/fokuskaefig.test.ts` ohne DOM beweist. Die
 * Verdrahtung an echte Elemente — `useFokuskaefig` — bleibt ungeprüft auf dieser
 * Ebene; die Playwright-Tour dafür fährt der Controller.
 */

/**
 * Der nächste Index in einer zyklischen Liste der Länge `anzahl`, ausgehend von
 * `aktuell`, in `richtung` (`1` = vorwärts/Tab, `-1` = rückwärts/Umschalt+Tab).
 *
 * `anzahl <= 0` liefert `-1` — es gibt kein nächstes Element in einer leeren
 * Liste, und `-1` ist kein gültiger Index, an dem ein Aufrufer versehentlich
 * weiterrechnen könnte.
 */
export function naechsterFokusIndex(anzahl: number, aktuell: number, richtung: 1 | -1): number {
  if (anzahl <= 0) return -1;
  // Zweifach modulo statt einer Verzweigung auf `< 0`: JavaScripts `%` liefert
  // bei einem negativen Zwischenergebnis auch `-0` (z. B. `(0 + -1) % 1`), und
  // `-0 < 0` ist FALSCH — die Verzweigung gäbe `-0` unverändert zurück. Der
  // zweite `% anzahl` normalisiert das mit, weil sein Dividend danach nicht
  // mehr negativ ist.
  return (((aktuell + richtung) % anzahl) + anzahl) % anzahl;
}

/**
 * Was als „fokussierbar" zählt — dieselbe Handvoll Selektoren, die jede
 * verbreitete Fokuskäfig-Implementierung benutzt. `[tabindex]:not([tabindex="-1"])`
 * schliesst eigens ausgenommene Elemente (wie den Dialog-Container selbst) aus.
 */
const FOKUSSIERBAR_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function fokussierbareElemente(wurzel: HTMLElement): readonly HTMLElement[] {
  return Array.from(wurzel.querySelectorAll<HTMLElement>(FOKUSSIERBAR_SELECTOR));
}

/**
 * Hält den Tastaturfokus im Dialog fest, solange er offen ist.
 *
 * - **Beim Öffnen** geht der Fokus auf das erste fokussierbare Element im
 *   INHALT (`inhaltRef`) — also das erste Feld der Maske, nicht die
 *   „Abbrechen"-Taste im Kopf, die im DOM zuerst steht, aber nicht das „erste
 *   sinnvolle Element" ist. Enthält der Inhalt nichts Fokussierbares (z. B. ein
 *   reiner Hinweistext), fällt der Fokus auf das erste Element im ganzen Dialog
 *   und zuletzt auf den Dialog-Container selbst (`tabIndex={-1}` in `Dialog`).
 * - **Tab/Umschalt+Tab** springt zyklisch innerhalb des Dialogs (`dialogRef`) —
 *   am letzten Element geht es zum ersten, am ersten zum letzten. Ohne
 *   fokussierbares Element bleibt der Fokus auf dem Container.
 * - **Beim Schliessen** (Unmount) geht der Fokus zurück an das Element, das ihn
 *   vor dem Öffnen hielt — den Auslöser. Ein Dialog, der schliesst, ohne den
 *   Fokus zurückzugeben, lässt Tastatur- und Screenreader-Nutzer am Anfang der
 *   Seite stehen.
 */
export function useFokuskaefig(
  dialogRef: RefObject<HTMLElement | null>,
  inhaltRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return undefined;

    const ausloeser = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const inhaltsElemente = inhaltRef.current === null ? [] : fokussierbareElemente(inhaltRef.current);
    const ziel = inhaltsElemente[0] ?? fokussierbareElemente(dialog)[0] ?? dialog;
    ziel.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const elemente = fokussierbareElemente(dialog);
      if (elemente.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const aktuellerIndex = elemente.indexOf(document.activeElement as HTMLElement);
      const naechsterIndex = naechsterFokusIndex(
        elemente.length,
        aktuellerIndex === -1 ? 0 : aktuellerIndex,
        event.shiftKey ? -1 : 1,
      );
      event.preventDefault();
      elemente[naechsterIndex]?.focus();
    };

    dialog.addEventListener('keydown', onKeyDown);
    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      ausloeser?.focus();
    };
  }, [dialogRef, inhaltRef]);
}
